import React     from 'react';
import PropTypes from 'prop-types';

// Assets
import * as S            from './product-single-row.module.scss';
import APPLE_APP_ICON    from '../../../../assets/imgs/third-party/app-store-black.svg';
import GOOGLE_APP_ICON   from '../../../../assets/imgs/third-party/google-play-store.svg';

// Components
import { ProductName } from '../../../common/product/prod-name';
import { Expiry }      from '../../../common/product/expiry';
import { TagLine }     from '../../../common/product/tag-line';
import { Price }       from '../../../common/product/price';
import { CancelDowngradeBox } from '../../../modals/change-plans/cancel-downgrade';

// Selectors
import * as UserSelectors    from '../../../../context/selectors/user-selectors';
import * as ProductSelectors from '../../../../context/selectors/product-selectors';
import * as BillSelectors    from '../../../../context/selectors/bill-selectors';

// Context Functions
import { setCtxNestedObj,
  mergeSubscriptionData
} from '../../../../context/ctx-actions';
import { useGlobalCtx }       from '../../../../context/ctx-hook';
import { getBillManagerType } from '../../../../context/selectors/bill-selectors';

// Helpers
import * as StateConstants from '../../../../helpers/constants/state-constants';
import { useAutoRenew }    from '../../../../helpers/hooks/use-auto-renew';
import { isInBetween }     from '../../../../helpers/time-helpers';
import * as RouteHelpers   from '../../../../helpers/route-helper';
import * as TimeHelpers    from '../../../../helpers/time-helpers';
import * as Tealium        from '../../../../helpers/tealium';
import * as Constants      from '../../../../helpers/constants/constants';


// API calls
import * as NetworkCalls from '../../../../network/network-calls';

// Constants
import { BM_ACTIONS } from '../../../../helpers/constants/constants';

const getExternalSubMsg = (SUB) => {
  if (!SUB.active) {
    return {
      title: 'Expired Subscription',
      message: 'This subscription is no longer active',
    };
  } else if (!SUB.autoRenew) {
    return {
      title: 'Renewal Canceled',
      message: 'Your subscription has been canceled but it is still valid until your expiration date. In order to avoid expiration, you must resume your subscription in the app store.'
    };
  } else {
    return false;
  }
};

const getSubMsg = (SUB, hasPayment) => {
  if (!SUB.active) {
    return {
      title: 'Expired Subscription',
      message: 'This subscription is no longer active',
    };
  } else if (!hasPayment) {
    return {
      title: 'No Payment Method',
      message: 'In order to not lose your connected services, add a payment method for renewal'
    };
  } else if (!SUB.autoRenew) {
    return {
      title: 'Renewal Canceled',
      message: 'Your subscription has been canceled but it is still valid until your expiration date. In order to avoid expiration, you must resume your subscription.'
    };
  } else {
    return false;
  }
};

const SingleProductRow = ({isModal = false, showPrice = true, sub, priceOverride = false, testId}) => {
  const {globalDispatch, state} = useGlobalCtx();
  const {
    sku,
    renewalSku,
    active,
    autoRenew,
    tierUriName,
    renewalFrequency,
    expireOn,
    effectiveAt,
    isInTrialPeriod,
    country
  } = sub;

  // Bill manager shenanigans
  // const QKN_SUB = ProductSelectors.getSubscription(state, state.country === 'us' ? 'quicken-us' : 'quicken-ca');
  const QKN_SUB_DATA = BillSelectors.getQknSubscription(state, state?.country);
  const QKN_SUB = QKN_SUB_DATA?.bundleSub ? QKN_SUB_DATA?.bundleSub : QKN_SUB_DATA;
  const BM_TYPE = getBillManagerType(state);

  const BM_MAX_ENT = QKN_SUB ? BillSelectors.getMaxEntitlement(state) : false;
  const BM_DATA = QKN_SUB ? BillSelectors.getBmTierData(BM_MAX_ENT) : false;
  const HIDE_RESUME_SUB = ['quicken-online-backup'].includes(sub?.productLineUriName) && QKN_SUB?.autoRenew === false;

  const PRODUCT_PAYMENT = UserSelectors.getProductsPaymentMethod(sub.productLineUriName, state);
  const HAS_ACH         = PRODUCT_PAYMENT ? (PRODUCT_PAYMENT?.paymentMethodType === 'ACH'): false;
  const ACH_DECLINED = (HAS_ACH ? UserSelectors.getUserAchIsDeclined(sub?.productLineUriName, state) : false);

  const buyNowSku = state.skus?.[renewalSku] ? renewalSku : state.defaultSkus?.[state.country]?.[sub?.tierUriName];

  // const SUB = ProductSelectors.getSubscription(state, sub.productLineUriName);
  // console.log(state);

  const HAS_PAYMENT = UserSelectors.getProductsPaymentMethod(sub, state);

  // Trial Logic
  const HISTORY_LIST = UserSelectors.getUserSubHistory(state, sub?.productLineUriName);
  const IS_CANCELLED = React.useMemo(
    () =>
      !sub.active || (!autoRenew && HISTORY_LIST?.filter(({ action, createdAt }) =>
        (action === 'DISABLE_AUTORENEW' && isInBetween(sub.effectiveAt, expireOn, createdAt))
      ).length > 0),
    [autoRenew, effectiveAt, expireOn, HISTORY_LIST]
  );

  const EXP_WITHIN_6_MONTHS = TimeHelpers.expWithin6MonthsOfToday(expireOn);

  // const DISCOUNTED_RENEWAL = isInTrialPeriod && sub.renewalSku ? ProductSelectors.getDiscountedDataBySku(state, sub.renewalSku) : false;
  // const HAS_EXPIRED = (HISTORY_LIST?.filter(({ action }) =>  (( action === 'EXPIRE' || action==='HARD_CANCEL'))).length > 0);
  // const DISPLAY_PRICE = HAS_EXPIRED ? (DISCOUNTED_RENEWAL.DisplayFullPrice ? (DISCOUNTED_RENEWAL.DisplayFullPrice) : PRICE ) : PRICE;

  // Extracted trial label logic to account for apparent design changes, may need another look
  const SHOW_TRIAL_LABEL = isInTrialPeriod && (!IS_CANCELLED || sub.active === true);
  const PRODUCT_LOGO = ProductSelectors.getProductLogo(sub);

  const PENDING_DOWNGRADE = sub?.renewalTierUriName ? sub?.renewalTierUriName !== sub?.tierUriName : false;
  const IS_ADDON = sub?.productLineUriName === 'billmanager' || sub?.productLineUriName === 'quicken-online-backup';
  const PARENT_ACTIVE = IS_ADDON ? QKN_SUB?.active : sub?.active;
  const IS_ADDON_AND_PARENT_ACTIVE = IS_ADDON && PARENT_ACTIVE;
  // console.log('sub?.productLineUriName:', sub?.productLineUriName);
  // console.log('QKN_SUB:', QKN_SUB);
  // console.log('IS_ADDON:', IS_ADDON);
  // console.log('PARENT_ACTIVE:', PARENT_ACTIVE);
  // console.log('IS_ADDON_AND_PARENT_ACTIVE:', IS_ADDON_AND_PARENT_ACTIVE);


  const isExternal = sub?.subscriptionExternal;
  const IS_APPLE = isExternal ? sub?.productLineUriName === 'acme-apple' : false;
  const STORE = IS_APPLE ? 'Apple App Store' : 'Google Play Store';

  const handleAROn = useAutoRenew(sub?.productLineUriName, true);

  const updatePaymentModal = React.useCallback(
    (sub) => globalDispatch(setCtxNestedObj('modal', {
      type:       StateConstants.MODALS.UPDATE_PAYMENT,
      ctx:        { product: sub, paymentOnly: false, isDefaultPayment: true},
      isLoading:  true,
      hasError:   false,
      isDisabled: false
    })),
    [globalDispatch]
  );

  const manageSubscription = React.useCallback(
    async () => globalDispatch(setCtxNestedObj('modal', {
      type:       StateConstants.MODALS.SUBSCRIPTION,
      ctx:        {product: [sub]},
      isLoading:  true,
      hasError:   false,
      isDisabled: false
    })),
    [globalDispatch]
  );

  const handleUpdateBilling = React.useCallback(
    () => globalDispatch(setCtxNestedObj('modal', {
      type: StateConstants.MODALS.UPDATE_PAYMENT,
      ctx: { product: sub?.productLineUriName, paymentOnly: false, isDefaultPayment: true },
      isLoading: true,
      hasError: false,
      isDisabled: false
    })),
    [globalDispatch, sub?.productLineUriName]
  );


  const handleUBmpdateBilling = React.useCallback(
    () => globalDispatch(setCtxNestedObj('modal', {
      type:       StateConstants.MODALS.UPDATE_PAYMENT,
      ctx:        {
        product:     sub.productLineUriName,
        plan:        tierUriName ?? null,
        paymentOnly: false
      },
      isLoading:  true,
      hasError:   false,
      isDisabled: false
    })),
    [globalDispatch, tierUriName]
  );

  const handleAchDeclined = React.useCallback(
    () => globalDispatch(setCtxNestedObj('modal', {
      type: StateConstants.MODALS.UPDATE_PAYMENT,
      isLoading: true,
      hasError: false,
      isDisabled: false,
      ctx: {
        product: sub.productLineUriName,
        paymentOnly: true
      }
    })),
    [globalDispatch, tierUriName]
  );

  const reActivateSubscription = async () => {
    const {data} = await NetworkCalls.callPostChangeTier(tierUriName, sku, BM_ACTIONS.CREATE);
    // Tealium.tealiumLink(Tealium.BMTealiumEvent(state, data));
    Tealium.gtmLink(Tealium.BMTealiumEvent(state, data));

    globalDispatch(mergeSubscriptionData(sub.productLineUriName, data.postState));
  };

  return (
    <div className={S.subTileContain}>
      <div className={S.subTileLeft}>
        <div className={S.productRow}>
          <img className={S.rowLogo} alt="square product logo" src={PRODUCT_LOGO}></img>
          <div className={S.productName}>
            <div className={S.productTitle}>
              <ProductName sub={sub} product={sub.productName} className={S.acmeName} nameStyle='inline'/>
              {SHOW_TRIAL_LABEL && (<span className={S.trialHeading}>Free trial</span>)}
            </div>

            <TagLine uriName={sub.tierUriName} className={S.tagLine} />
          </div>
        </div>

        {['quicken-us', 'quicken-ca'].includes(sub.productLineUriName) && BM_TYPE ==='FREE' && BM_DATA &&
          <div className={S.productRow}>
            <img className={S.rowLogo} alt="square product logo" src={ProductSelectors.getProductLogo(sub)}></img>
            <div className={S.productName}>
              <ProductName sub={BM_DATA} product={BM_DATA.productName} className={S.acmeName} nameStyle='inline'/>
              <TagLine uriName={BM_DATA.tier} className={S.tagLine} />
            </div>
          </div>
        }

        {/* Account message for external subscriptions */}
        {sub.subscriptionExternal && getExternalSubMsg(sub) &&
          <div className={S.msgs}>
            <div className={S.productAlert}>
              <p className={S.messageTitle}>{getExternalSubMsg(sub).title}</p>
              <p>{getExternalSubMsg(sub).message}</p>
            </div>
          </div>
        }

        {/* Account message for internal subscriptions */}
        {!sub.subscriptionExternal && getSubMsg(sub, HAS_PAYMENT) &&
          <div className={S.msgs}>
            <div className={S.productAlert}>
              <p className={S.messageTitle}>{getSubMsg(sub, HAS_PAYMENT).title}</p>
              <p>{getSubMsg(sub, HAS_PAYMENT).message}</p>
            </div>
          </div>
        }

        {PENDING_DOWNGRADE && sub?.autoRenew && sub?.productLineUriName !== 'billmanager' &&
          <CancelDowngradeBox product={sub?.productLineUriName} className={'mainTile'} />
        }
      </div>


      <div className={S.subTileRight}>
        {!isModal && !sub?.subscriptionExternal &&
          <div className={`${S.actionBtnWrapper}`} >
            {!isModal && sub.autoRenew && sub.active && HAS_PAYMENT && !(sub.productLineUriName == 'billmanager' && ACH_DECLINED) && (
              <button
                type="button"
                data-testid={`BTN_${testId}_MANAGE_SUB`}
                onClick={manageSubscription}
                className={S.ctaBtnInvert}
              >Manage Subscription</button>
            )}

            {!isModal && !HAS_PAYMENT && active && (
              <button
                type="button"
                data-testid={`BTN_${testId}_ADD_BILLING`}
                onClick={handleUpdateBilling}
                className={S.ctaBtn}
              >Add Billing</button>
            )}

            {sub.active && !sub.autoRenew && HAS_PAYMENT && !HIDE_RESUME_SUB && (!IS_ADDON || IS_ADDON_AND_PARENT_ACTIVE) && (
              <>
                <button
                  type="button"
                  data-testid={`${testId}_RESTART_SUB`}
                  onClick={HAS_PAYMENT ? () => handleAROn(sub)  : () => updatePaymentModal(sub)}
                  className={HIDE_RESUME_SUB ? `${S.ctaBtn} ${S.disabled}` : S.ctaBtn}
                  disabled={HIDE_RESUME_SUB ? true : state.isDisabled}
                >Resume Subscription</button>
              </>
            )}


            {/* Buy now buttons and BM specific buy now buttons */}
            {!sub.active && sub.productLineUriName !== 'billmanager' && (
              <a
                data-testid={`LINK_RENEW_${testId}`}
                className={S.ctaBtn}
                // href={RouteHelpers.getEStoreRenewal(state.country)}
                href={RouteHelpers.getEStoreCartUrlAddProduct(buyNowSku, state.country)}
              >Buy Now</a>
            )}

            {sub.productLineUriName == 'billmanager' && (QKN_SUB?.active && !sub?.active) && (
              <button
                type="button"
                onClick={HAS_ACH ? reActivateSubscription : handleUBmpdateBilling}
                className={S.ctaBtn}
                data-testid="BTN_BM_RENEW_NOW"
              >Reactivate</button>
            )}

            {/* BM specific use case for someone with declined ACH to add new ACH info */}
            {sub.productLineUriName == 'billmanager' && sub?.active && sub?.autoRenew && HAS_ACH && ACH_DECLINED && (
              <button
                type="button"
                onClick={handleAchDeclined}
                className={S.ctaBtn}
                data-testid="BTN_BM_MANAGE_DECLINED_SUB"
              >Fix Billing</button>
            )}

          </div>
        }

        {isModal && showPrice && (
          priceOverride ? (
            <Price
              renewalFrequency={sub?.renewalFrequency}
              price={priceOverride}
              className={S.currentPrice}
            />
          ) : (
            <Price
              renewalFrequency={sub?.renewalFrequency}
              sku={sub?.renewalSku}
              className={S.currentPrice}
            />
          )
        )}

        { HIDE_RESUME_SUB && !sub?.subscriptionExternal &&
          <p className={S.resumeParent}>Resume parent subscription first.</p>
        }

        {sub.subscriptionExternal &&
          <>
            <a
              target="_parent"
              className={IS_APPLE ? S.appleIcon : S.googleIcon}
              href={IS_APPLE ? Constants.APP_SUB_PAGE.APPLE : Constants.APP_SUB_PAGE.GOOGLE}
            >
              <img className={S.appStoreIcon} src={IS_APPLE ? APPLE_APP_ICON : GOOGLE_APP_ICON} alt={STORE}/>
            </a>
            <p className={S.subText}>*Manage your subscription at {STORE}</p>
          </>
        }

        <Expiry sub={sub} product={sub.productLineUriName} className={S.productExpiry} />

        {isModal && ['quicken-us', 'quicken-ca'].includes(sub?.productLineUriName) && (EXP_WITHIN_6_MONTHS) && !sub?.subscriptionExternal && (
          <a
            className={S.extendPlan}
            href={RouteHelpers.getEStoreRenewal(state.country, sub.productLineUriName, tierUriName)}
            data-testid="LINK_EXTEND_MEMBERSHIP"
          >Extend Membership</a>
        )}



      </div>
    </div>
  );
};


SingleProductRow.displayName  = 'SingleProductRow';
SingleProductRow.propTypes    = {
  isModal: PropTypes.bool,
  showPrice: PropTypes.bool,
  sub:     PropTypes.object,
  testId:  PropTypes.string,
  priceOverride: PropTypes.oneOf(['bool', 'string'])
};
SingleProductRow.defaultProps = {
  isModal: false,
  subs: []
};

export { SingleProductRow };